import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppService } from '../../../app.service';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import { ValidationRuleEditModalComponent } from './validation-rule-edit-modal/validation-rule-edit-modal.component';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

@Component({
    selector: 'gtw-rules-engine',
    templateUrl: './rules-engine.component.html',
    styleUrls: ['./rules-engine.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RulesEngineComponent implements OnInit, OnDestroy {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;
  clients: any[] = [];
  selectedClientKey: any = '';

  private readonly ACTION_CODE = 'd4yabw';
  private readonly GRID_ID = '102';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private dialog: MatDialog,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.setupClients();
    this.buildGridConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    const rowData = event.data?.rowData ?? event.data;
    this.openEditModal({
      RULE_KEY: rowData?.DROOLSRULEKEY,
      RULE_NAME: rowData?.DROOLSRULENAME,
      RULE_DESC: rowData?.DROOLSRULEDESCRIPTION,
      RULE_PATH: rowData?.DROOLSRULESESSION,
    });
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  onClientChange(): void {
    this.gridConfig = {
      ...this.gridConfig,
      extraParams: { client_key: this.selectedClientKey },
    };
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  createRule(): void {
    this.openEditModal(null);
  }

  private openEditModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '90vw';
    config.disableClose = true;
    config.data = {
      rowData: rowData || {},
      mode: rowData?.RULE_KEY ? 'edit' : 'new',
      clientKey: this.selectedClientKey,
    };

    this.adminScreenContext.setRowData(rowData);
    const ref = this.dialog.open(ValidationRuleEditModalComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.adminScreenContext.setRowData({});
        if (result === 'saved') {
          this.adminGrid?.clearCache();
          this.adminGrid?.refresh({ client_key: this.selectedClientKey });
        }
      });
  }

  private setupClients(): void {
    const user = this.appService.USER_SETTINGS?.user;
    const userClients = user?.clients;
    const userClient = user?.client;
    if (Array.isArray(userClients) && userClients.length > 0) {
      this.clients = userClients;
    } else if (userClient?.client_key !== undefined) {
      this.clients = [userClient];
    }

    this.selectedClientKey = user?.client_key
      ?? this.clients?.[0]?.client_key
      ?? sessionStorage.getItem('client_key')
      ?? '';
  }

  private buildGridConfig(): void {
    this.gridConfig = {
      actionCode: this.ACTION_CODE,
      gridId: this.GRID_ID,
      extraParams: { client_key: this.selectedClientKey },
      autoLoad: true,
      cacheKey: 'admin/rules-engine',
    };
  }
}
